import { useQuery } from '@tanstack/react-query';
import type { AxiosError } from 'axios';

import type { PlaceDetail, PlaceDetailsResponse } from 'src/app/types';

import { CACHE_KEY_PHARMACY_DETAILS, CACHE_KEY_PHARMACY_PHOTO, CACHE_KEY_PHARMACY_SEARCH } from './_keys';
import type { BFFResponse } from './useBffClient';
import { useBffClient } from './useBffClient';

interface PlaceSearchData {
  html_attributions: Array<string>;
  next_page_token?: string;
  results: Array<PlaceDetail>;
  status: string;
}
export interface PlaceSearchResponse extends BFFResponse {
  data: PlaceSearchData;
}

export interface PlacePhotoResponse extends BFFResponse {
  data: string;
}

export const usePharmacySearch = (
  query: string,
  location: string,
  radius?: number, // in meters
  additionalParams?: { [key: string]: string },
  custom_cache_key?: string,
  enabled = false
) => {
  const client = useBffClient();
  const params = {
    query: `${query}${query.length < 6 ? ' pharmacy' : ''}`,
    location,
    radius: radius || 5000,
    type: 'establishment',
    region: 'us',
    ...additionalParams,
  };
  const results = useQuery<PlaceSearchResponse, AxiosError>(
    [custom_cache_key ?? CACHE_KEY_PHARMACY_SEARCH, query],
    () => client.get('/search/place/textsearch', { params }),
    {
      refetchOnWindowFocus: false,
      enabled: enabled,
      staleTime: Infinity,
    }
  );
  return results;
};

export function usePlacePhoto(photoReference?: string, maxWidth = '150') {
  const client = useBffClient();
  const params = {
    photo_reference: photoReference,
    maxwidth: maxWidth,
  };

  const results = useQuery<PlacePhotoResponse, AxiosError>(
    [CACHE_KEY_PHARMACY_PHOTO, photoReference],
    () => client.get('search/place/photo', { params }),
    {
      enabled: !!photoReference,
      staleTime: Infinity,
    }
  );
  return results;
}

export function usePlaceDetail(placeId: string, custom_cache_key?: string, enabled = false) {
  const client = useBffClient();

  const results = useQuery<PlaceDetailsResponse, AxiosError>(
    [custom_cache_key ?? CACHE_KEY_PHARMACY_DETAILS, placeId],
    () =>
      client
        .get('search/place/details', {
          params: { place_id: placeId, fields: 'formatted_address,opening_hours,formatted_phone_number,name,place_id' },
        })
        .then(resp => resp.data?.result),
    {
      enabled: enabled,
      retry: 1,
      staleTime: Infinity,
    }
  );
  return results;
}
